import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Container,
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import axios from 'axios';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotDialogOpen, setForgotDialogOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMessage, setForgotMessage] = useState({ type: '', text: '' });

  // Mensaje de sesión expirada desde el state de navegación
  const sessionMessage = location.state?.message;

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await login(formData.email, formData.password);

      // ✅ Redirigir según el rol del usuario
      const userRole = response.user.role;

      if (userRole === 'owner') {
        navigate('/owner/dashboard');
      } else if (userRole === 'admin') {
        navigate('/dashboard');
      } else {
        navigate('/dashboard'); // Fallback
      }
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!forgotEmail) {
      setForgotMessage({ type: 'error', text: 'Por favor ingrese su correo' });
      return;
    }

    setForgotLoading(true);
    setForgotMessage({ type: '', text: '' });

    try {
      // Usar la URL base sin duplicar /api/ si ya está incluido en la variable de entorno,
      // o ajustar según convenga. Aquí asumimos que REACT_APP_API_URL es el host (ej: http://localhost:3001)
      // Si REACT_APP_API_URL incluye /api, entonces debemos quitarlo de la cadena hardcodeada.
      // Solución robusta: usar una función helper o simplemente corregir asumiendo el estándar.

      const baseUrl = process.env.REACT_APP_API_URL || '';
      // Si baseUrl termina en /api, no agregar otro /api
      const endpoint = baseUrl.endsWith('/api')
        ? `${baseUrl}/auth/forgot-password`
        : `${baseUrl}/api/auth/forgot-password`;

      const response = await axios.post(endpoint, {
        email: forgotEmail
      });

      setForgotMessage({
        type: 'success',
        text: response.data.message || 'Solicitud enviada correctamente'
      });
      setTimeout(() => {
        if (response.data.success) setForgotDialogOpen(false);
      }, 3000);
    } catch (err) {
      setForgotMessage({
        type: 'error',
        text: err.response?.data?.message || 'Error al procesar la solicitud'
      });
    } finally {
      setForgotLoading(true); // Se queda en true hasta que el usuario cierra o vuelve a intentar?
      // Corregido: setForgotLoading(false);
      setForgotLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {/* LOGO DE SISTEMA */}
            <Box
              component="img"
              src="/logo_villas.jpg"
              alt="Logo"
              sx={{
                width: '100%',
                maxWidth: 280,
                height: 'auto',
                mb: 3,
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
            />
            <Typography component="h1" variant="h5" gutterBottom>
              Portal de Acceso
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Bienvenido
            </Typography>
          </Box>

          {sessionMessage && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {sessionMessage}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Correo Electrónico"
              name="email"
              autoComplete="email"
              autoFocus
              value={formData.email}
              onChange={handleChange}
              disabled={loading}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Contraseña"
              type="password"
              id="password"
              autoComplete="current-password"
              value={formData.password}
              onChange={handleChange}
              disabled={loading}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Iniciar Sesión'}
            </Button>
          </Box>

          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Link
              component="button"
              variant="caption"
              onClick={() => {
                setForgotDialogOpen(true);
                setForgotMessage({ type: '', text: '' });
              }}
              sx={{ cursor: 'pointer', textDecoration: 'none' }}
            >
              ¿Olvidaste tu contraseña? Haz clic aquí
            </Link>
          </Box>
        </Paper>
      </Box>

      {/* DIÁLOGO DE RECUPERACIÓN */}
      <Dialog open={forgotDialogOpen} onClose={() => !forgotLoading && setForgotDialogOpen(false)}>
        <DialogTitle>Recuperar Contraseña</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Ingrese su correo electrónico registrado. Le enviaremos una contraseña temporal.
          </DialogContentText>
          {forgotMessage.text && (
            <Alert severity={forgotMessage.type} sx={{ mb: 2 }}>
              {forgotMessage.text}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            id="forgot-email"
            label="Correo Electrónico"
            type="email"
            fullWidth
            variant="outlined"
            value={forgotEmail}
            onChange={(e) => setForgotEmail(e.target.value)}
            disabled={forgotLoading}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setForgotDialogOpen(false)} disabled={forgotLoading}>
            Cancelar
          </Button>
          <Button
            onClick={handleForgotPassword}
            variant="contained"
            disabled={forgotLoading || !forgotEmail}
          >
            {forgotLoading ? <CircularProgress size={24} /> : 'Enviar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Login;
